import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../models/models'; // Ensure you define a User model
import api from './api';

interface UserFilters {
  owner_id: number | null;
  limit: number;
  offset: number;
}

interface UserState {
  userData: User[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedUser: User | null;
  filters: UserFilters;
}

const initialState: UserState = {
  userData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedUser: null,
  filters: {
    owner_id: null,
    limit: 5,
    offset: 0,
  },
};

// Fetch users by owner
export const fetchUsersByOwner = createAsyncThunk<
  { data: User[]; total: number; totalPages: number; currentPage: number },
  { limit: number; offset: number },
  { rejectValue: string }
>(
  'user/fetchUsersByOwner',
  async ({limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/users/by-owner', {
        params: {limit, offset }
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch users.');
    }
  }
);

// Fetch user by ID
export const fetchUserById = createAsyncThunk<User, number, { rejectValue: string }>(
  'user/fetchUserById',
  async (userId, thunkAPI) => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'User not found.');
    }
  }
);

// Create a user
export const createUser = createAsyncThunk<User, { name: string; email: string; password: string }, { rejectValue: string }>(
  'user/createUser',
  async (userData, thunkAPI) => {
    try {
      const response = await api.post('/users/create', userData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create user.');
    }
  }
);

// Create or update a user (admin management)
export const createOrUpdateUser = createAsyncThunk<
  { id: number },
  { id: number; login: string; password?: string; email: string; phone: string; is_active: number; role_id?: number },
  { rejectValue: string }
>(
  'user/createOrUpdateUser',
  async (userData, thunkAPI) => {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || "Échec de l'enregistrement.");
    }
  }
);

// Delete a user
export const deleteUser = createAsyncThunk<void, number, { rejectValue: string }>(
  'user/deleteUser',
  async (userId, thunkAPI) => {
    try {
      await api.delete('/users', { params: { id: userId } });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la suppression.');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    clearUserData: (state) => {
      state.userData = [];
    },
    clearUserMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setUserFilters: (state, action: PayloadAction<Partial<UserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetUserFilters: (state) => {
      state.filters = {
        owner_id: null,
        limit: 5,
        offset: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsersByOwner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsersByOwner.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchUsersByOwner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.successMessage = 'User created successfully!';
        state.userData.push(action.payload); // Optionally update list
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createOrUpdateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createOrUpdateUser.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Utilisateur enregistré avec succès.';
      })
      .addCase(createOrUpdateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Utilisateur supprimé avec succès.';
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setSelectedUser,
  clearUserData,
  clearUserMessages,
  setUserFilters,
  resetUserFilters
} = userSlice.actions;

export default userSlice.reducer;
